/**
 * Account and access helpers for Composition API components.
 *
 * These mirror the checks the Vue app root exposes (`this.$root.isLotusAccount()`,
 * `this.$root.isSystemAdminUser()`, `this.$root.hasAccess()`), which `<script setup>`
 * components can't reach. Access control itself still goes through
 * `globalStore.currentUser.hasFeatureAccess(featureCode)`.
 */

import globalStore from "/globalStore.js";


// Lotus's own account. Referencing the ID isn't lovely, but it's what the app
// root has always done and it's now in one place rather than two.
export const LOTUS_ACCOUNT_ID = "875E733C-09AA-EA11-9B04-0050F297923B";


/**
 * Is an account the Lotus account?
 *
 * @param {string} accountId ID of the account to check. Defaults to the account the current user is signed in to.
 */
export function isLotusAccount(accountId)
{
	if (!accountId) accountId = globalStore.state.currentUser.activeAccountId;
	if (!accountId) return false;

	return accountId.toLowerCase() == LOTUS_ACCOUNT_ID.toLowerCase();
}


/**
 * Is the current session a Lotus user aliasing someone else?
 */
export function isLotusAlias()
{
	return globalStore.state.currentUser.isAliasLogin;
}


/**
 * Can the current user access a feature?
 */
export function hasAccess(featureCode)
{
	if (!globalStore.currentUser) return false;

	return globalStore.currentUser.hasFeatureAccess(featureCode);
}


/**
 * Is the current user a Lotus administrator or developer?
 */
export function isSystemAdminUser()
{
	return hasAccess("LotusDevelopment.All") || hasAccess("LotusAdministration.All");
}


/**
 * Can the current user administer the people in the account?
 *
 * Matches the API: every People endpoint needs AccountAdmin.All (or Lotus staff). AccountAdmin.Users
 * on its own isn't enough, so it isn't checked here.
 */
export function canAdministerUsers()
{
	return hasAccess("AccountAdmin.All") || isSystemAdminUser();
}
